// ImportsPage — Imports/event-imports page.
// Manual tab: paste-bar + history feed. POSTs to /api/manual_imports which
// creates a LinkImport per URL and enqueues a LinkImportJob. Progress + final
// state come back over ActionCable (ImportsChannel) so the UI updates in real
// time and survives reload.
// Auto tab: persisted CRUD against /api/auto_imports. Last-run timestamps
// are derived server-side to follow the team's Tue/Thu cadence.

import React, { useState, useRef, useEffect, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { fetchData, postData, putData, deleteData } from "../utils/api"
import consumer from "../channels/consumer"

// ─── style helpers ────────────────────────────────────────────────

const importsBtn = {
  primary:  { background: "#0F172A", color: "#fff", border: 0, borderRadius: 10, padding: "10px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer", transition: "background 150ms cubic-bezier(.2,.8,.2,1)" },
  ghost:    { background: "#fff", color: "#0F172A", border: "1px solid #E2E8F0", borderRadius: 10, padding: "9px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer" },
  text:     { background: "transparent", color: "#64748B", border: 0, padding: "6px 8px", fontWeight: 600, fontSize: 13, cursor: "pointer" },
  iconOnly: { background: "transparent", color: "#64748B", border: 0, padding: 8, borderRadius: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "all 120ms cubic-bezier(.2,.8,.2,1)" },
}

const fieldStyle = {
  width: "100%", display: "block",
  border: "1px solid #E2E8F0", borderRadius: 10,
  padding: "11px 14px", fontSize: 14,
  color: "#0F172A", background: "#fff", outline: "none", boxSizing: "border-box",
  transition: "border-color 120ms cubic-bezier(.2,.8,.2,1), box-shadow 120ms cubic-bezier(.2,.8,.2,1)",
}

const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#0F172A", marginBottom: 6, letterSpacing: "-0.01em" }

const FieldHint = ({ children }) => (
  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6, lineHeight: 1.4 }}>{children}</div>
)

// One-time keyframe injection
function useInjectedStyles() {
  useEffect(() => {
    if (typeof document === "undefined") return
    if (document.getElementById("imports-kf")) return
    const s = document.createElement("style")
    s.id = "imports-kf"
    s.textContent = `
      @keyframes imports-spin { to { transform: rotate(360deg) } }
      @keyframes imports-fade-in { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: none } }
      @keyframes imports-pop { 0% { transform: scale(.94); opacity: 0 } 100% { transform: scale(1); opacity: 1 } }
      @keyframes imports-shake { 0%, 100% { transform: translateX(0) } 25% { transform: translateX(-3px) } 75% { transform: translateX(3px) } }
      .imports-card-enter { animation: imports-fade-in 220ms cubic-bezier(.2,.8,.2,1) both }
      .imports-pop { animation: imports-pop 180ms cubic-bezier(.2,.8,.2,1) both }
      .imports-icon-btn:hover { background: #F1F5F9 !important; color: #0F172A !important }
      .imports-icon-btn-danger:hover { background: #FEE2E2 !important; color: #DC2626 !important }
      .imports-input:focus { border-color: #0F172A !important; box-shadow: 0 0 0 3px rgba(15,23,42,.08) }
      .imports-card { transition: box-shadow 180ms cubic-bezier(.2,.8,.2,1), transform 180ms cubic-bezier(.2,.8,.2,1) }
    `
    document.head.appendChild(s)
  }, [])
}

// ─── visual atoms ─────────────────────────────────────────────────

const Spinner = ({ color = "#0F172A", size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ animation: "imports-spin 800ms linear infinite" }}>
    <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeOpacity="0.18" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
  </svg>
)

const Icon = {
  edit:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  trash:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>,
  link:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>,
  check:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
  refresh: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>,
  pause:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>,
  play:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>,
  alert:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
  plus:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  ext:     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>,
  x:       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
}

const StatusPill = ({ kind, children }) => {
  const styles = {
    importing: { bg: "#DBEAFE", fg: "#1D4ED8" },
    success:   { bg: "#DCFCE7", fg: "#16A34A" },
    error:     { bg: "#FEE2E2", fg: "#DC2626" },
    warning:   { bg: "#FEF3C7", fg: "#B45309" },
    paused:    { bg: "#F1F5F9", fg: "#64748B" },
    active:    { bg: "#DCFCE7", fg: "#16A34A" },
  }[kind] || { bg: "#F1F5F9", fg: "#64748B" }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 9999, background: styles.bg, color: styles.fg, fontSize: 12, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap" }}>{children}</span>
  )
}

const ProgressBar = ({ pct, color = "#2563EB" }) => (
  <div style={{ width: "100%", height: 6, background: "#E2E8F0", borderRadius: 99, overflow: "hidden" }}>
    <div style={{ width: `${Math.min(100, Math.max(0, pct))}%`, height: "100%", background: color, transition: "width 350ms cubic-bezier(.2,.8,.2,1)" }} />
  </div>
)

const isValidUrl = (s) => {
  if (!s || !s.trim()) return false
  try { const u = new URL(s.trim()); return u.protocol === "http:" || u.protocol === "https:" }
  catch (e) { return false }
}

const hostOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, "") } catch (e) { return url || "" } }

// Apply an Uploadcare CDN-side resize when possible so we send a small image
// over the wire instead of the full-resolution banner. Strips any existing
// transformation chain to avoid stacking weirdness. Falls back to the raw URL
// for non-Uploadcare hosts.
const uploadcareThumb = (url, size) => {
  if (!url) return url
  const m = url.match(/^(https?:\/\/[^/]+\/[^/]+)\//)
  if (!m || !/ucarecdn\.com|ucarecd\.net|ucr\.io/.test(m[1])) return url
  return `${m[1]}/-/scale_crop/${size}x${size}/center/-/format/auto/`
}

// ─── ManualPasteBar ───────────────────────────────────────────────

function ManualPasteBar({ onImport }) {
  const [value, setValue] = useState("")
  const [error, setError] = useState("")
  const taRef = useRef(null)

  // Always treat input as multi-line. One URL = one import, many URLs = many.
  // Splits on any whitespace so users can paste comma-/space-/newline-separated
  // lists without thinking about format.
  const lines = value.split(/\s+/).map(s => s.trim()).filter(Boolean)
  const validCount = lines.filter(isValidUrl).length

  const submit = () => {
    if (!lines.length) return
    const bad = lines.filter(l => !isValidUrl(l))
    if (bad.length) {
      setError(bad.length === lines.length ? "Please paste a full URL starting with http:// or https://" : `${bad.length} of these aren't valid URLs.`)
      const el = taRef.current
      if (el) {
        el.style.animation = "imports-shake 240ms cubic-bezier(.36,.07,.19,.97)"
        setTimeout(() => { if (el) el.style.animation = "" }, 260)
      }
      return
    }
    onImport(lines)
    setValue("")
    setError("")
  }

  const submitLabel = lines.length <= 1 ? "Import event" : `Import ${validCount || lines.length} events`

  return (
    <div className="imports-card" style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 4px 14px rgba(15,23,42,.06)", padding: 18 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, background: "#F1F5F9", color: "#0F172A", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto", marginTop: 6 }}>{Icon.link}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={{ ...labelStyle, fontSize: 13 }}>Import event(s)</label>
          <textarea
            ref={taRef}
            className="imports-input"
            placeholder={"Paste one or more event URLs (one per line):\nhttps://eventbrite.com/e/jazz-fest-844720011\nhttps://withfriends.co/event/19284"}
            value={value}
            onChange={(e) => { setValue(e.target.value); setError("") }}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit() }}
            rows={3}
            style={{ ...fieldStyle, fontFamily: "var(--vs-font-mono, monospace)", fontSize: 13, lineHeight: 1.5, resize: "vertical", minHeight: 80, borderColor: error ? "#DC2626" : "#E2E8F0" }}
          />
          {error
            ? <div style={{ fontSize: 12, color: "#DC2626", marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>{Icon.alert} {error}</div>
            : <FieldHint>One specific event URL per line — ticketing page or event listing. Not a venue homepage or organizer profile.</FieldHint>
          }
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button
              onClick={submit}
              disabled={!value.trim()}
              style={{ ...importsBtn.primary, opacity: value.trim() ? 1 : 0.4, cursor: value.trim() ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── DefaultImageCard ─────────────────────────────────────────────

// Lets the company set / change a fallback image used when neither the
// OpenAI scrape nor the Smooth scrape can find an event-specific image.
// Calls window.uploadcare.openDialog directly — no widget input rendered,
// so we have a single button instead of stacking the widget's own UI on
// top of ours.
function DefaultImageCard({ value, onChange }) {
  const openDialog = () => {
    if (typeof window.uploadcare === "undefined") return
    try {
      const dialog = window.uploadcare.openDialog(null, { imagesOnly: true, multiple: false })
      dialog.done((file) => {
        if (!file) return
        file.done((info) => {
          const url = info.cdnUrl || (info.uuid ? `https://ucarecdn.com/${info.uuid}/` : null)
          if (url) onChange(url)
        })
      })
    } catch (e) {
      console.warn("DefaultImageCard openDialog failed:", e)
    }
  }

  return (
    <div className="imports-card"
      style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 4px 12px rgba(15,23,42,.06)", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
      {value ? (
        <img src={uploadcareThumb(value, 80)} alt="" width={40} height={40}
             style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", flex: "0 0 auto", background: "#F1F5F9" }} />
      ) : (
        <div style={{ width: 40, height: 40, borderRadius: 8, border: "1.5px dashed #CBD5E1", color: "#94A3B8", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
          {Icon.plus}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
          {value ? "Fallback image set" : "No fallback image set"}
        </div>
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
          Used as the event banner when we can't find an event-specific image on the source page.
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "0 0 auto" }}>
        <button onClick={openDialog} style={importsBtn.ghost}>{value ? "Replace" : "Set image"}</button>
        {value && (
          <button onClick={() => onChange(null)} style={{ ...importsBtn.text, color: "#DC2626" }}>Remove</button>
        )}
      </div>
    </div>
  )
}

// ─── ManualFeedRow ────────────────────────────────────────────────

// Server statuses: pending, scraping, classifying, building, completed, failed
const IN_PROGRESS_STATUSES = new Set(["pending", "scraping", "classifying", "building"])

function ManualFeedRow({ row, onRetry, onCancel }) {
  const status = row.status
  const inProgress = IN_PROGRESS_STATUSES.has(status)
  const completed = status === "completed"
  const failed = status === "failed"
  const cancelled = status === "cancelled"
  const warnings = Array.isArray(row.warning_messages) ? row.warning_messages : []
  const hasWarnings = completed && warnings.length > 0
  const draftHref = row.event_edit_url || (row.event_id ? "#" : null)

  return (
    <div className="imports-card imports-card-enter"
      style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 4px 12px rgba(15,23,42,.06)", padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
      {row.event_image_url && (
        <img
          src={uploadcareThumb(row.event_image_url, 72)}
          alt=""
          width={36}
          height={36}
          style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", flex: "0 0 auto", background: "#F1F5F9" }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#0F172A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 360 }}>
            {completed && row.event_name ? row.event_name : hostOf(row.url)}
          </span>
          {inProgress && <StatusPill kind="importing"><Spinner color="#1D4ED8" size={11} /> {row.stage_message || "Importing"}</StatusPill>}
          {completed && <StatusPill kind="success">{Icon.check} Imported</StatusPill>}
          {hasWarnings && <StatusPill kind="warning">{Icon.alert} Needs review</StatusPill>}
          {failed && <StatusPill kind="error">{Icon.alert} Failed</StatusPill>}
          {cancelled && <StatusPill kind="paused">Cancelled</StatusPill>}
        </div>
        <div style={{ fontSize: 12, color: "#64748B", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 380 }}>{row.url}</span>
        </div>
        {inProgress && (
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
            <ProgressBar pct={row.progress || 0} />
            <span style={{ fontSize: 11, color: "#64748B", fontWeight: 600, minWidth: 32, textAlign: "right" }}>{Math.round(row.progress || 0)}%</span>
          </div>
        )}
        {hasWarnings && (
          <div style={{ fontSize: 12, color: "#B45309", marginTop: 6 }}>
            {warnings.join(" ")}
          </div>
        )}
        {failed && (
          <div style={{ fontSize: 12, color: "#DC2626", marginTop: 4 }}>
            {row.error_message || "We couldn't read that page. Check the URL."}
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, flex: "0 0 auto" }}>
        {completed && draftHref && (
          <a href={draftHref}
             style={{ color: hasWarnings ? "#B45309" : "#0F172A", fontSize: 13, fontWeight: 700, textDecoration: "none", padding: "7px 12px", borderRadius: 8, display: "inline-flex", alignItems: "center", gap: 6, transition: "background 120ms", border: hasWarnings ? "1px solid #FCD34D" : "none", background: hasWarnings ? "#FFFBEB" : "transparent" }}
             onMouseEnter={(e) => e.currentTarget.style.background = hasWarnings ? "#FEF3C7" : "#F1F5F9"}
             onMouseLeave={(e) => e.currentTarget.style.background = hasWarnings ? "#FFFBEB" : "transparent"}>
            View draft {Icon.ext}
          </a>
        )}
        {failed && (
          <button onClick={onRetry} style={{ ...importsBtn.ghost, padding: "7px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
            {Icon.refresh} Retry
          </button>
        )}
        {cancelled && (
          <button onClick={onRetry} style={{ ...importsBtn.ghost, padding: "7px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
            {Icon.refresh} Retry
          </button>
        )}
        {inProgress && (
          <button onClick={onCancel} title="Cancel import"
                  style={{ background: "transparent", color: "#64748B", border: "1px solid #E2E8F0", padding: "7px 10px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, transition: "all 120ms" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#FEE2E2"; e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.borderColor = "#FCA5A5" }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#64748B"; e.currentTarget.style.borderColor = "#E2E8F0" }}>
            {Icon.x} Cancel
          </button>
        )}
      </div>
    </div>
  )
}

// ─── AutoImportCard ───────────────────────────────────────────────

function AutoImportCard({ row, onSave, onRemove, onTogglePause }) {
  const [expanded, setExpanded] = useState(!row.saved)
  const [draft, setDraft] = useState({ label: row.label || "", url: row.url || "", params: row.params || "" })
  const [errors, setErrors] = useState({})

  const submit = () => {
    const next = {}
    if (!draft.label.trim()) next.label = "Give this source a name."
    if (!isValidUrl(draft.url.trim())) next.url = "Please paste a full URL."
    setErrors(next)
    if (Object.keys(next).length) return
    onSave({ ...draft, label: draft.label.trim(), url: draft.url.trim(), params: draft.params.trim() })
    setExpanded(false)
  }

  if (row.saved && !expanded) {
    const params = (row.params || "").split(",").map(p => p.trim()).filter(Boolean)
    const paused = !!row.paused
    return (
      <div className="imports-card imports-card-enter"
        style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 4px 12px rgba(15,23,42,.06)", padding: "16px 14px", display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 12, opacity: paused ? 0.7 : 1 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: "#0F172A" }}>{row.label}</span>
            {paused
              ? <StatusPill kind="paused">{Icon.pause} Paused</StatusPill>
              : <StatusPill kind="active"><span style={{ width: 6, height: 6, borderRadius: 99, background: "#16A34A" }} /> Active</StatusPill>}
          </div>
          <div style={{ fontSize: 12, color: "#64748B", display: "flex", alignItems: "center", gap: 6, marginBottom: params.length ? 6 : 0, flexWrap: "wrap" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>{hostOf(row.url)}</span>
            <span style={{ color: "#CBD5E1" }}>·</span>
            <span>Imports run Tuesdays &amp; Thursdays</span>
            <span style={{ color: "#CBD5E1" }}>·</span>
            <span>Last run <strong style={{ color: "#0F172A", fontWeight: 600 }}>{row.last_run || "never"}</strong></span>
            {typeof row.last_count === "number" && <><span style={{ color: "#CBD5E1" }}>·</span><span style={{ fontWeight: 600 }}>{row.last_count} imported</span></>}
          </div>
          {params.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {params.map((p, i) => (
                <span key={i} style={{ fontSize: 11, color: "#0F172A", background: "#F1F5F9", borderRadius: 6, padding: "2px 8px", fontWeight: 500, fontFamily: "var(--vs-font-mono, monospace)" }}>{p}</span>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <button onClick={onTogglePause} className="imports-icon-btn" style={importsBtn.iconOnly} title={paused ? "Resume" : "Pause"}>
            {paused ? Icon.play : Icon.pause}
          </button>
          <button onClick={() => setExpanded(true)} className="imports-icon-btn" style={importsBtn.iconOnly} title="Edit">{Icon.edit}</button>
          <button onClick={onRemove} className="imports-icon-btn imports-icon-btn-danger" style={importsBtn.iconOnly} title="Remove">{Icon.trash}</button>
        </div>
      </div>
    )
  }

  return (
    <div className="imports-card imports-card-enter" style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 4px 12px rgba(15,23,42,.06)", padding: 20 }}>
      <div style={{ display: "grid", gap: 14 }}>
        <div>
          <label style={labelStyle}>Name</label>
          <input className="imports-input" type="text" placeholder="e.g. Slowdown · Omaha venue" value={draft.label}
            onChange={(e) => { setDraft(d => ({ ...d, label: e.target.value })); setErrors(er => ({ ...er, label: "" })) }}
            style={{ ...fieldStyle, borderColor: errors.label ? "#DC2626" : "#E2E8F0" }} />
          {errors.label && <div style={{ fontSize: 12, color: "#DC2626", marginTop: 6 }}>{errors.label}</div>}
        </div>
        <div>
          <label style={labelStyle}>Source URL</label>
          <input className="imports-input" type="url" placeholder="https://www.eventbrite.com/o/your-organizer-page" value={draft.url}
            onChange={(e) => { setDraft(d => ({ ...d, url: e.target.value })); setErrors(er => ({ ...er, url: "" })) }}
            style={{ ...fieldStyle, borderColor: errors.url ? "#DC2626" : "#E2E8F0" }} />
          {errors.url
            ? <div style={{ fontSize: 12, color: "#DC2626", marginTop: 6 }}>{errors.url}</div>
            : <FieldHint>The Eventbrite organizer page, venue events page, or any listing we should re-check for new events.</FieldHint>}
        </div>
        <div>
          <label style={labelStyle}>Filters <span style={{ color: "#94A3B8", fontWeight: 500 }}>· optional</span></label>
          <textarea className="imports-input" placeholder="jazz, concerts, 21+, downtown" value={draft.params}
            onChange={(e) => setDraft(d => ({ ...d, params: e.target.value }))} rows={2}
            style={{ ...fieldStyle, fontFamily: "var(--vs-font-mono, monospace)", fontSize: 13, resize: "vertical" }} />
          <FieldHint>Plain-text keywords, comma-separated. We'll only import events that match.</FieldHint>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        {row.saved && <button onClick={() => { setExpanded(false); setDraft({ label: row.label || "", url: row.url || "", params: row.params || "" }); setErrors({}) }} style={importsBtn.ghost}>Cancel</button>}
        {!row.saved && onRemove && <button onClick={onRemove} style={importsBtn.text}>Remove</button>}
        <button onClick={submit} style={importsBtn.primary}>{row.saved ? "Save changes" : "Save auto-import"}</button>
      </div>
    </div>
  )
}

function AddAnotherTile({ onClick, label, sub }) {
  return (
    <button onClick={onClick} style={{
      width: "100%", textAlign: "left",
      background: "transparent", border: "1.5px dashed #CBD5E1",
      borderRadius: 12, padding: "16px 18px", cursor: "pointer",
      display: "flex", alignItems: "center", gap: 12,
      transition: "all 180ms cubic-bezier(.2,.8,.2,1)", color: "#64748B",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#0F172A"; e.currentTarget.style.color = "#0F172A"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.boxShadow = "0 1px 2px rgba(15,23,42,.04), 0 4px 12px rgba(15,23,42,.06)" }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#CBD5E1"; e.currentTarget.style.color = "#64748B"; e.currentTarget.style.background = "transparent"; e.currentTarget.style.boxShadow = "none" }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(15,23,42,.05)", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>{Icon.plus}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2, fontWeight: 500 }}>{sub}</div>}
      </div>
    </button>
  )
}

function EmptyState({ icon, title, body, ctaLabel, onCta }) {
  return (
    <div style={{ background: "#fff", border: "1.5px dashed #E2E8F0", borderRadius: 14, padding: "36px 24px", textAlign: "center" }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: "#F1F5F9", color: "#64748B", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>{icon}</div>
      <h3 style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 4px", color: "#0F172A" }}>{title}</h3>
      <p style={{ fontSize: 14, color: "#64748B", margin: "0 auto 16px", maxWidth: 380, lineHeight: 1.5 }}>{body}</p>
      <button onClick={onCta} style={importsBtn.primary}>{ctaLabel}</button>
    </div>
  )
}

function ConfirmDialog({ open, title, body, confirmLabel, danger, onConfirm, onCancel }) {
  if (!open) return null
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="imports-pop" style={{ background: "#fff", borderRadius: 14, padding: 22, maxWidth: 420, width: "100%", boxShadow: "0 8px 24px rgba(15,23,42,.10), 0 24px 48px rgba(15,23,42,.14)" }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 6px" }}>{title}</h3>
        <p style={{ fontSize: 14, color: "#64748B", margin: "0 0 18px", lineHeight: 1.5 }}>{body}</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={importsBtn.ghost}>Cancel</button>
          <button onClick={onConfirm} style={{ ...importsBtn.primary, background: danger ? "#DC2626" : "#0F172A" }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  )
}

function ToastStack({ toasts, onDismiss }) {
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1100, display: "flex", flexDirection: "column", gap: 8, pointerEvents: "none" }}>
      {toasts.map(t => (
        <div key={t.id} className="imports-pop" style={{ pointerEvents: "auto", background: "#0F172A", color: "#fff", borderRadius: 10, padding: "12px 14px", boxShadow: "0 8px 24px rgba(15,23,42,.20)", display: "flex", alignItems: "center", gap: 10, minWidth: 240, maxWidth: 380 }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: t.kind === "error" ? "#DC2626" : t.kind === "success" ? "#22C55E" : "#6A3AFB" }} />
          <div style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{t.message}</div>
          <button onClick={() => onDismiss(t.id)} style={{ background: "transparent", border: 0, color: "rgba(255,255,255,.7)", cursor: "pointer", padding: 2 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
      ))}
    </div>
  )
}

// ─── ImportsPage ─────────────────────────────────────────────────

export default function ImportsPage({ companyId, companySlug } = {}) {
  useInjectedStyles()

  // Mirrors the pattern in MetaAdsAIPage — `is-admin` is set as an attribute
  // on #react-root by the EventImports HAML view based on current_user.admin?
  // Read-only here; the server-side guard on the toggle endpoint is the
  // actual security boundary (this just hides the UI for non-admins).
  const [isAdmin] = useState(() => {
    const root = typeof document !== "undefined" ? document.getElementById("react-root") : null
    return root ? root.getAttribute("is-admin") === "true" : false
  })

  // Tab is reflected in the URL as ?tab=manual|auto|managed so any of them
  // are deep-linkable (e.g. for support to point a customer straight to the
  // Vesta Managed flow). Defaults to "manual" when the param is missing or
  // unrecognized.
  const [searchParams, setSearchParams] = useSearchParams()
  const TAB_IDS = ["manual", "auto", "managed"]
  const tabFromUrl = TAB_IDS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "manual"
  const tab = tabFromUrl
  const setTab = (next) => {
    const params = new URLSearchParams(searchParams)
    if (next === "manual") params.delete("tab"); else params.set("tab", next)
    setSearchParams(params, { replace: false })
  }
  const [toasts, setToasts] = useState([])
  const [confirm, setConfirm] = useState(null)
  const [manual, setManual] = useState([])
  const [manualHasMore, setManualHasMore] = useState(false)
  const [manualPage, setManualPage] = useState(1)
  const [manualLoadingMore, setManualLoadingMore] = useState(false)
  const [auto, setAuto] = useState([])
  const [autoLoaded, setAutoLoaded] = useState(false)
  const [defaultImageUrl, setDefaultImageUrl] = useState(null)
  const [autoImportsActive, setAutoImportsActive] = useState(false)

  const pushToast = useCallback((message, kind = "info") => {
    const id = Date.now() + Math.random()
    setToasts(ts => [...ts, { id, message, kind }])
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 3200)
  }, [])
  const dismissToast = (id) => setToasts(ts => ts.filter(t => t.id !== id))

  const mergeRow = useCallback((incoming) => {
    setManual(rows => {
      const idx = rows.findIndex(r => r.id === incoming.id)
      if (idx === -1) return [incoming, ...rows]
      const next = rows.slice()
      next[idx] = { ...next[idx], ...incoming }
      return next
    })
  }, [])

  // Load manual imports history on mount. (The Auto tab is currently a
  // static link to vesta.fun/autoimport — see the Auto tab body below — so
  // we skip the auto_imports fetch. When we revive self-serve auto imports,
  // restore the fetchData("api/auto_imports") call here.)
  useEffect(() => {
    fetchData("api/manual_imports", { page: 1 })
      .then(data => {
        setManual(data.manual_imports || [])
        setDefaultImageUrl(data.default_event_image_url || null)
        setAutoImportsActive(Boolean(data.auto_imports_active))
        setManualHasMore(Boolean(data.pagination && data.pagination.has_more))
        setManualPage(1)
      })
      .catch(() => {})
  }, [])

  // Polling fallback. ActionCable's `async` adapter (used in dev) only delivers
  // broadcasts within the same process, so messages from the DJ worker never
  // reach the browser. Polling the index every 3s while any row is in progress
  // makes the UI correct in dev and resilient to dropped broadcasts in prod.
  useEffect(() => {
    const anyInProgress = manual.some(r => IN_PROGRESS_STATUSES.has(r.status))
    if (!anyInProgress) return
    const t = setInterval(() => {
      // Polling only fetches page 1. In-progress rows are always among the
      // most recent so we'll catch their updates here; older paginated rows
      // we've already loaded are preserved by the merge below.
      fetchData("api/manual_imports", { page: 1 })
        .then(data => {
          const incoming = data.manual_imports || []
          setManual(prev => {
            const byId = new Map(incoming.map(r => [r.id, r]))
            const merged = prev.map(r => byId.has(r.id) ? { ...r, ...byId.get(r.id) } : r)
            const known = new Set(merged.map(r => r.id))
            const additions = incoming.filter(r => !known.has(r.id))
            return [...additions, ...merged]
          })
        })
        .catch(() => {})
    }, 3000)
    return () => clearInterval(t)
  }, [manual])

  // ActionCable subscription — receives progress/completed/failed broadcasts
  // emitted by LinkImports::Runner.
  useEffect(() => {
    if (!consumer || !companyId) return
    const subscription = consumer.subscriptions.create(
      { channel: "ImportsChannel", company_id: companyId },
      {
        received(payload) {
          if (!payload || !payload.link_import_id) return
          mergeRow({
            id: payload.link_import_id,
            status: payload.status,
            stage_message: payload.stage_message,
            progress: payload.progress,
            event_id: payload.event_id,
            event_edit_url: payload.event_edit_url,
            event_name: payload.event_name,
            warnings: payload.warnings || [],
            warning_messages: payload.warning_messages || [],
            error_message: payload.error_message,
          })
          if (payload.action === "completed") {
            pushToast(payload.event_name ? `Draft created: ${payload.event_name}` : "Draft created.", "success")
          } else if (payload.action === "failed") {
            pushToast(payload.error_message || "Import failed.", "error")
          }
        },
        connected() {},
        disconnected() {},
      }
    )
    return () => { try { subscription.unsubscribe() } catch (e) {} }
  }, [companyId, mergeRow, pushToast])

  const importManual = async (urls) => {
    try {
      const data = await postData("api/manual_imports", { manual_import: { urls } })
      const newRows = data.manual_imports || []
      setManual(rows => [...newRows, ...rows])
      if (newRows.length > 1) pushToast(`Importing ${newRows.length} events…`, "info")
    } catch (err) {
      pushToast("Couldn't queue imports. Please try again.", "error")
    }
  }

  const retryManual = (id) => async () => {
    try {
      const data = await postData(`api/manual_imports/${id}/retry`, {})
      if (data && data.manual_import) mergeRow(data.manual_import)
    } catch (err) {
      pushToast("Couldn't retry. Please try again.", "error")
    }
  }

  const cancelManual = (id) => async () => {
    try {
      const data = await postData(`api/manual_imports/${id}/cancel`, {})
      if (data && data.manual_import) mergeRow(data.manual_import)
    } catch (err) {
      pushToast("Couldn't cancel. Please try again.", "error")
    }
  }

  const loadMoreManual = useCallback(async () => {
    if (manualLoadingMore || !manualHasMore) return
    setManualLoadingMore(true)
    try {
      const next = manualPage + 1
      const data = await fetchData("api/manual_imports", { page: next })
      const incoming = data.manual_imports || []
      setManual(prev => {
        const known = new Set(prev.map(r => r.id))
        const additions = incoming.filter(r => !known.has(r.id))
        return [...prev, ...additions]
      })
      setManualHasMore(Boolean(data.pagination && data.pagination.has_more))
      setManualPage(next)
    } catch (err) {
      pushToast("Couldn't load more imports.", "error")
    } finally {
      setManualLoadingMore(false)
    }
  }, [manualPage, manualHasMore, manualLoadingMore, pushToast])

  const toggleAutoImportsActive = useCallback(async (next) => {
    const previous = autoImportsActive
    setAutoImportsActive(next) // optimistic
    try {
      const data = await putData("api/manual_imports/auto_imports_active", { auto_imports_active: next })
      setAutoImportsActive(Boolean(data.auto_imports_active))
      pushToast(next ? "Concierge banner enabled for this company." : "Concierge banner disabled.", "success")
    } catch (err) {
      setAutoImportsActive(previous) // rollback
      pushToast("Couldn't update — make sure you're signed in as admin.", "error")
    }
  }, [autoImportsActive, pushToast])

  const saveDefaultImage = useCallback(async (url) => {
    setDefaultImageUrl(url) // optimistic
    try {
      const data = await putData("api/manual_imports/default_image", { default_event_image_url: url })
      setDefaultImageUrl(data.default_event_image_url || null)
      pushToast(url ? "Fallback image saved." : "Fallback image removed.", "success")
    } catch (err) {
      pushToast("Couldn't save the fallback image.", "error")
    }
  }, [pushToast])

  const addAuto = () => setAuto(rows => [...rows, { id: `draft-${Date.now()}`, label: "", url: "", params: "", saved: false }])

  const saveAuto = (id) => async (patch) => {
    const existing = auto.find(r => r.id === id)
    try {
      if (existing && existing.saved) {
        const data = await putData(`api/auto_imports/${id}`, { auto_import: patch })
        setAuto(rows => rows.map(r => r.id === id ? { ...data.auto_import, saved: true } : r))
        pushToast("Auto-import updated.", "success")
      } else {
        const data = await postData("api/auto_imports", { auto_import: patch })
        setAuto(rows => rows.map(r => r.id === id ? { ...data.auto_import, saved: true } : r))
        pushToast("Auto-import saved. We'll check it shortly.", "success")
      }
    } catch (err) {
      pushToast("Couldn't save. Please try again.", "error")
    }
  }

  const removeAuto = (id) => () => {
    const row = auto.find(r => r.id === id)
    if (!row) return
    if (!row.saved) { setAuto(rows => rows.filter(r => r.id !== id)); return }
    setConfirm({
      title: `Remove "${row.label}"?`,
      body: "We'll stop watching this source. Events already imported will stay in your dashboard.",
      confirmLabel: "Remove",
      danger: true,
      onConfirm: async () => {
        try {
          await deleteData(`api/auto_imports/${id}`)
          setAuto(rows => rows.filter(r => r.id !== id))
          setConfirm(null)
          pushToast("Source removed.", "info")
        } catch (err) {
          pushToast("Couldn't remove. Please try again.", "error")
          setConfirm(null)
        }
      }
    })
  }

  const togglePause = (id) => async () => {
    try {
      const data = await postData(`api/auto_imports/${id}/toggle_pause`, {})
      setAuto(rows => rows.map(r => r.id === id ? { ...data.auto_import, saved: true } : r))
      const wasPaused = auto.find(r => r.id === id)?.paused
      pushToast(wasPaused ? "Source resumed." : "Source paused.", "info")
    } catch (err) {
      pushToast("Couldn't update. Please try again.", "error")
    }
  }

  const manualActive = manual.length
  const autoActive = auto.filter(r => r.saved).length
  const importingCount = manual.filter(r => IN_PROGRESS_STATUSES.has(r.status)).length

  // Auto tab is intentionally minimal in this release — UI surfaces a link
  // to the team's Typeform for setting up auto-imports concierge-style.
  // The full CRUD UI (AutoImportCard etc.) is preserved in this file for
  // when we revisit and ship self-serve auto-imports.
  //
  // Vesta Managed tab is a temporary bridge linking out to vesta.fun/import
  // (the legacy "send us your list and we'll import them" flow) for customers
  // who aren't ready to drive imports themselves. Removable once self-serve
  // confidence is high enough.
  const tabs = [
    { id: "manual",  label: "Manual",        count: manualActive },
    { id: "auto",    label: "Auto",          count: null },
    { id: "managed", label: "Vesta Managed", count: null },
  ]

  return (
    <div style={{ padding: "32px 40px 56px", maxWidth: 1040, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, marginBottom: 22, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.03em", margin: "0 0 6px" }}>Imports</h1>
          <p style={{ color: "#64748B", margin: 0, fontSize: 15, maxWidth: 560 }}>Pull events from anywhere on the web. Add them one at a time, or set up sources that import automatically.</p>
        </div>
        {/* Header CTA is hidden on the Auto tab in this release — see Auto tab body below. */}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ display: "inline-flex", background: "#fff", borderRadius: 9999, padding: 4, boxShadow: "0 1px 2px rgba(15,23,42,.06)" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              border: 0, padding: "9px 22px", borderRadius: 9999,
              fontWeight: 600, fontSize: 14, cursor: "pointer",
              background: tab === t.id ? "#0F172A" : "transparent",
              color: tab === t.id ? "#fff" : "#0F172A",
              display: "flex", alignItems: "center", gap: 8,
              transition: "background 150ms cubic-bezier(.2,.8,.2,1), color 150ms cubic-bezier(.2,.8,.2,1)",
            }}>
              {t.label}
              {t.count != null && (
                <span style={{ background: tab === t.id ? "rgba(255,255,255,.18)" : "#F1F5F9", color: tab === t.id ? "#fff" : "#64748B", fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 9999 }}>{t.count}</span>
              )}
            </button>
          ))}
        </div>
        {tab === "manual" && importingCount > 0 && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "#1D4ED8", fontWeight: 600 }}>
            <Spinner color="#1D4ED8" size={12} />
            {importingCount} {importingCount === 1 ? "import" : "imports"} running
          </div>
        )}
      </div>

      {tab === "manual" && (
        <div style={{ display: "grid", gap: 18 }}>
          <DefaultImageCard value={defaultImageUrl} onChange={saveDefaultImage} />
          <ManualPasteBar onImport={importManual} />
          {manual.length === 0
            ? (
              <div style={{ padding: "36px 20px", textAlign: "center", color: "#64748B", background: "#fff", borderRadius: 14, boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 4px 12px rgba(15,23,42,.06)" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A", marginBottom: 4 }}>No imports yet</div>
                <div style={{ fontSize: 13 }}>Paste a link above to get started. Imported events show up here.</div>
              </div>
            )
            : (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 4px 10px", gap: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em" }}>Recent imports</div>
                  <div style={{ fontSize: 12, color: "#94A3B8" }}>
                    Showing {manual.length}{manualHasMore ? "" : " (all)"}
                  </div>
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {manual.map(row => (
                    <ManualFeedRow key={row.id} row={row} onRetry={retryManual(row.id)} onCancel={cancelManual(row.id)} />
                  ))}
                </div>
                {manualHasMore && (
                  <div style={{ marginTop: 14, textAlign: "center" }}>
                    <button onClick={loadMoreManual} disabled={manualLoadingMore}
                      style={{ ...importsBtn.ghost, padding: "9px 18px", fontSize: 13, opacity: manualLoadingMore ? 0.6 : 1, cursor: manualLoadingMore ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                      {manualLoadingMore && <Spinner color="#0F172A" size={12} />}
                      {manualLoadingMore ? "Loading…" : "Load more"}
                    </button>
                  </div>
                )}
                <div style={{ marginTop: 14, padding: "12px 16px", textAlign: "center", fontSize: 12, color: "#94A3B8" }}>
                  Older imports move into your dashboard — view them under <a href={companySlug ? `/companies/${companySlug}/events?event_status=draft&page=1` : "#"} style={{ color: "#0F172A", fontWeight: 600 }}>Events › Drafts</a>.
                </div>
              </div>
            )}
        </div>
      )}

      {tab === "auto" && (
        <div style={{ display: "grid", gap: 14 }}>
          {isAdmin && (
            <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: "#92400E", letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 8px", border: "1px solid #FCD34D", borderRadius: 4, background: "#FEF9C3", flex: "0 0 auto" }}>Admin only</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
                  Concierge auto-imports for this company
                </div>
                <div style={{ fontSize: 12, color: "#78350F", marginTop: 2 }}>
                  When ON, this company sees the green "your auto-imports are set up and running" banner. When OFF, they see the Typeform CTA.
                </div>
              </div>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", flex: "0 0 auto" }}>
                <input type="checkbox" checked={autoImportsActive}
                       onChange={(e) => toggleAutoImportsActive(e.target.checked)}
                       style={{ width: 18, height: 18, cursor: "pointer" }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{autoImportsActive ? "ON" : "OFF"}</span>
              </label>
            </div>
          )}
          {autoImportsActive ? (
          <div className="imports-card"
            style={{ background: "#fff", borderRadius: 14, padding: "28px 28px", boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 4px 14px rgba(15,23,42,.06)", display: "flex", alignItems: "center", gap: 18 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: "#DCFCE7", color: "#16A34A", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
              {Icon.check}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 4px", color: "#0F172A" }}>Your auto-imports are set up and running</h3>
              <p style={{ fontSize: 14, color: "#64748B", margin: 0, lineHeight: 1.5 }}>
                We're pulling new events from your configured sources on a schedule. Need to add a source, change filters, or pause an import? Email <a href="mailto:Support@eventvesta.com" style={{ color: "#0F172A", fontWeight: 600 }}>Support@eventvesta.com</a> and we'll take care of it.
              </p>
            </div>
          </div>
        ) : (
          <div className="imports-card"
            style={{ background: "#fff", borderRadius: 14, padding: "32px 28px", textAlign: "center", boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 4px 14px rgba(15,23,42,.06)" }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, background: "#F1F5F9", color: "#0F172A", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              {Icon.refresh}
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 8px", color: "#0F172A" }}>Set up an auto-import</h3>
            <p style={{ fontSize: 14, color: "#64748B", margin: "0 auto 20px", maxWidth: 460, lineHeight: 1.5 }}>
              Tell us about a venue, organizer, or calendar page you want pulled in automatically.
              We'll get it set up for you and your events will start appearing in your dashboard.
            </p>
            <a href="https://vesta.fun/autoimport" target="_blank" rel="noopener noreferrer"
               style={{ ...importsBtn.primary, display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
              Set up auto-imports {Icon.ext}
            </a>
          </div>
        )}
        </div>
      )}

      {tab === "managed" && (
        <div className="imports-card"
          style={{ background: "#fff", borderRadius: 14, padding: "32px 28px", textAlign: "center", boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 4px 14px rgba(15,23,42,.06)" }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: "#F1F5F9", color: "#0F172A", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            {Icon.link}
          </div>
          <h3 style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 8px", color: "#0F172A" }}>Have the Vesta team handle imports for you</h3>
          <p style={{ fontSize: 14, color: "#64748B", margin: "0 auto 20px", maxWidth: 480, lineHeight: 1.5 }}>
            Send us your event list and we'll get them imported for you. Best for ongoing batches or one-off bulk imports you'd rather not run yourself.
          </p>
          <a href="https://vesta.fun/import" target="_blank" rel="noopener noreferrer"
             style={{ ...importsBtn.primary, display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
            Submit imports to the Vesta team {Icon.ext}
          </a>
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onConfirm={() => confirm?.onConfirm && confirm.onConfirm()}
        onCancel={() => setConfirm(null)}
      />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  )
}
